// Date helpers shared by admin forms (native <input type="date">) and public
// display. Values are stored as plain strings — either a real ISO date
// ("YYYY-MM-DD", what new entries always write) or, for records created
// before the date picker existed, free text like "Jul 2026". We never rewrite
// stored values here; we only format/parse for display and for pre-filling
// <input type="date">, which requires strict ISO or it silently shows blank.

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_INDEX = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True if `value` is a strict "YYYY-MM-DD" the native date input understands. */
export function isIsoDate(value) {
    if (!value || typeof value !== "string") return false;
    const m = value.match(ISO_RE);
    if (!m) return false;
    const d = new Date(`${value}T00:00:00`);
    return !Number.isNaN(d.getTime());
}

/**
 * Best-effort parse of legacy free text ("Jul 2026", "Aug 12, 2025", …) into
 * an ISO date, for pre-filling the picker when editing an old record. Returns
 * "" when it can't confidently parse — the picker then shows empty rather
 * than guess, and the original text is preserved unless the admin picks a
 * new date (see DateField.jsx).
 */
export function toIsoInput(value) {
    if (!value || typeof value !== "string") return "";
    const s = value.trim();
    if (isIsoDate(s)) return s;

    let m = s.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s*(\d{4})$/); // "Jul 12, 2026" / "July 12 2026"
    if (m) {
        const mon = MONTH_INDEX[m[1].slice(0, 3).toLowerCase()];
        if (mon !== undefined) {
            return `${m[3]}-${String(mon + 1).padStart(2, "0")}-${String(Number(m[2])).padStart(2, "0")}`;
        }
    }
    m = s.match(/^([A-Za-z]{3,})\.?\s+(\d{4})$/); // "Jul 2026" (no day — default to the 1st)
    if (m) {
        const mon = MONTH_INDEX[m[1].slice(0, 3).toLowerCase()];
        if (mon !== undefined) return `${m[2]}-${String(mon + 1).padStart(2, "0")}-01`;
    }
    return "";
}

/** Pretty display, e.g. "12 Jul 2026". Falls back to the raw string untouched
 *  when it isn't a parseable date (legacy free text is never mangled). */
export function formatDate(value) {
    if (!value || typeof value !== "string") return "";
    const iso = isIsoDate(value) ? value : "";
    if (!iso) return value; // legacy free text — show as-is
    const [y, mo, d] = iso.split("-").map(Number);
    return `${d} ${MONTHS_SHORT[mo - 1]} ${y}`;
}

/**
 * "YYYY-MM-DD" -> a local-midnight Date, for calendar widgets. Deliberately
 * NOT `new Date(iso)` — that parses as UTC midnight, which renders as the
 * PREVIOUS day in any timezone behind UTC (a classic off-by-one bug).
 */
export function isoToLocalDate(iso) {
    if (!isIsoDate(iso)) return undefined;
    const [y, mo, d] = iso.split("-").map(Number);
    return new Date(y, mo - 1, d);
}

/** A Date -> "YYYY-MM-DD" using LOCAL parts (see isoToLocalDate for why). */
export function localDateToIso(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
}
